/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Location} from '@angular/common';
import {HttpParams} from '@angular/common/http';
import {AfterViewInit, Component, inject, OnInit, Optional} from '@angular/core';
import {AbstractControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {NbWindowRef} from '@nebular/theme';
import {FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import {ApiResponse, BaseComponent, BaseEntityModel, FileService, StoreService, Term} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {catchError, map, take, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {CrudEvent} from '../../interfaces/crud-event';
import {CrudConfig} from '../../interfaces/crud.config';
import {FormConfigService} from '../../services/form-config.service';

@Component({
  selector: 'app-create',
  templateUrl: './create-update.component.html',
  standalone: false
})
export class CreateUpdateComponent<T> extends BaseComponent implements OnInit, AfterViewInit {

  model: T & BaseEntityModel;
  permissionDenied = false;
  form = new UntypedFormGroup({});
  isModal = false;
  fields: FormlyFieldConfig[];
  pictureField: FormlyFieldConfig[];
  uploadPictureUri: string;
  uploadIconUri: string;
  options: FormlyFormOptions = {
    formState: {
      disabled: false,
    }
  };
  /**
   * @see configure()
   */
  editMode = false;
  showListButton = true;
  editName: string;

  redirectToEditPage = false;
  deleted = false;
  /**
   * The initialized model in beforeSubmit() method
   * Set in child component class
   */
  protected entity: T & BaseEntityModel;
  /**
   * Configures fields
   * Set in child component class
   */
  protected config: CrudConfig;

  /**
   * Indicates the format of request JSON or Form data
   * Set in child component class
   */
  protected isFormUpload: boolean;
  /**
   * Route param about entity edit case
   * Set configure method
   * @see configure()
   */
  protected id: string;
  protected createdId: string;
  protected validateHeaderUniqueness = false;

  storeService = inject(StoreService);

  constructor(protected readonly formConfigService: FormConfigService,
              protected readonly route: ActivatedRoute,
              protected readonly location: Location,
              @Optional() protected readonly windowRef: NbWindowRef,
              protected readonly fileService: FileService,
  ) {
    super();
  }

  /**
   *  Abstract methods implemented in inherited classes
   */
  afterSetModel(): void {
    // @ts-ignore
    this.editName = this.model?.header || this.editName || translations.edit;
  }

  beforeSubmit(): void {
    this.entity = this.serializeModel({...this.model});
  }

  afterSubmit(): void {
    if (this.redirectToEditPage && this.createdId !== undefined) {
      const extra = this.getAbsolutePath().startsWith('..') ? {relativeTo: this.route} : null;

      this.router.navigate([this.getAbsolutePath(), 'edit', this.createdId], extra);
    }
  }

  modalClose(): void {
    this.windowRef.close();
  }

  validate(): boolean {
    return true;
  }

  setConfig(config: CrudConfig) {
    const options = config.relatedEntity;
    if (!this.config.uri && this.uri) {
      this.config.uri = this.uri;
    }

    if (config.showListButton !== undefined) {
      this.showListButton = config.showListButton;
    }

    if (this.validateHeaderUniqueness) {
      config.columns = config.columns.map(column => {
        if (column.key === 'header') {
          column.asyncValidators = {
            notUnique: {
              expression: (c: AbstractControl) => this.uniqueHeaderChecker(c),
              message: () => translations.notHeaderUniqueError,
            }
          };
        }

        return column;
      });
    }

    this.formConfigService.setConfig(config.columns).formly();

    if (options !== undefined) {
      options.forEach(item => this.formConfigService.addMultipleOptions(item.multipleField, item.multipleEntity));
    }
  }

  ngOnInit(): void {
    this.setMode();
    this.configure();
  }

  ngAfterViewInit() {
    if (!this.editMode) {
      if (this.options?.resetModel) {
        this.options.resetModel();
      }
      this.loading = false;
      this.storeService.setTitle(this.getTitle());
    }

    this.cdr.detectChanges();
  }

  getTitle(onlyBase = false): string {
    const base = this.config.title;
    if (onlyBase) {
      return base;
    }

    if (this.editName) {
      return base + ' / ' + this.editName;
    }

    return base + ' / ' + translations.add;
  }

  /**
   * Restoring soft deleted entity
   */
  restore(): void {
    const result = this.compoApiService.patch<T, string>(this.uri + '/restore/' + String(this.id), null);

    result.pipe(take(1))
      .subscribe(success => {
        this.configure();
        this.handleSuccess(success);
      }, error => this.handleError(error));
  }

  /**
   *  Sends the entity data to backend
   *  adds id to url, if there's edititng mode
   *  BeforeSubmit is necessary sometimes and
   *  we can do something with entity before sending
   *  @see CreateUpdateManagerComponent component for example
   */
  onSubmit(): void {
    this.loading = true;
    this.beforeSubmit();
    let submitResult;

    if (this.isFormUpload === true) {
      submitResult = this.compoApiService.postForm<T, string>(this.uri + '/' + (this.id !== null ? String(this.id) : ''), this.entity);
    } else {
      submitResult = this.compoApiService.post<T, string>(this.uri + '/' + (this.id !== null ? String(this.id) : ''), this.entity);
    }

    submitResult.pipe(takeUntil(this.unsubscribe))
      .subscribe(success => this.handleSuccess(success), error => this.handleError(error));
  }

  pictureEvent(event: CrudEvent, type: string = null) {
    if (event === CrudEvent.UPLOADING) {
      this.loading = true;
    }
    if (event === CrudEvent.DELETE_PICTURE && type == 'icon') {
      this.model.deleteIcon = true;
      this.model.pictureField = null;
      this.redirectToEditPage = false;
      this.onSubmit();
    } else if (event === CrudEvent.DELETE_PICTURE) {
      this.model.deletePicture = true;
      this.model.pictureField = null;
      this.redirectToEditPage = false;
      this.onSubmit();
    }
    if (event === CrudEvent.UPDATE_PICTURE) {
      this.loadData();
      this.loading = false;
    }
  }

  /**
   * Entity can be having terms.
   * There's several standard methods for working with it
   * @param term
   */
  removeTerm(term: Term) {
    const index = this.model.terms.findIndex(el => el.id === term.id);
    this.model.terms.splice(index, 1);
    this.onSubmit();
  }

  insertTerm(term: Term) {
    if (0 === term.id) {
      this.saveTerm(term);
    } else {
      if (!this.model.terms.some(terms => terms.id === term.id)) {
        this.model.terms.push(term);
        this.onSubmit();
      } else {
        this.warningToast(translations.relationExists);
      }
    }
  }

  handleError(err) {
    this.loading = false;
    if (err?.status === 403) {
      this.permissionDenied = true;
    } else if (err?.error?.data) {
      this.errorToast(err.error.data);
    } else {
      return super.handleError(err);
    }
  }

  back(): void {
    this.location.back();
  }

  /**
   * Set creating or editing mode
   * @protected
   */
  protected setMode(): void {
    if (this.config.id !== undefined) {
      this.id = this.config.id;
    } else {
      this.id = this.route.snapshot.paramMap.get('id');
    }

    if (this.id !== null) {
      this.editMode = true;
    }

    if (this.config.relatedEntity !== undefined || this.editMode === true) {
      this.loading = true;
    }

  }

  protected configure() {
    if (!this.fields?.length) {
      this.fields = this.formConfigService.getConfig();
    }

    this.pictureField = this.formConfigService.getPictureConfig();
    if (this.editMode) {
      this.uploadPictureUri = this.uri + '/' + this.id + '/picture';
      this.uploadIconUri = this.uri + '/' + this.id + '/icon';
      this.loadData();
    } else {
      this.model = {} as T;
    }
  }

  protected loadData(): void {
    this.loading = true;
    this.compoApiService.get<T>(this.uri + '/' + this.id)
      .pipe(
        take(1),
        map(response => response.data),
        catchError(err => this.handleError(err)),
      )
      .subscribe(entity => this.setModel(entity));
  }

  /**
   * Sets data to current model when it's edit mode happening
   * @param model Any data for update
   *
   * After setModel is necessary sometimes.
   * For example, when we need to receive additional data related
   * to current entity.
   * @see CreateUpdateManagerComponent
   */
  protected setModel(model: T): void {
    this.model = this.deserializeModel(model);
    this.deleted = this.model.deleted;
    this.afterSetModel();
    this.loading = false;
    this.storeService.setTitle(this.getTitle());

    this.cdr.detectChanges();
  }

  protected deserializeModel<T>(obj: any): T {
    if (typeof obj === 'object') {
      for (const key in obj) {
        if (typeof obj[key] === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj[key])) {
          obj[key] = new Date(obj[key]) as T;
        }
      }
    }

    return obj;
  }

  protected serializeModel<T>(obj: any): T {
    if (typeof obj === 'object') {
      for (const key in obj) {
        if (obj[key] instanceof Date) {
          obj[key] = this.compoApiService.convertDateTime(obj[key]);
        }
      }
    }

    return obj;
  }

  protected handleSuccess(response: ApiResponse<string>) {
    this.loading = false;

    if (!response.success) {
      this.errorToast(response.data || response.message);
      this.cdr.detectChanges();
      return;
    }

    const message = (this.editMode ? translations.updated : translations.created);

    this.successToast(message);

    if (this.redirectToEditPage && (!this.editMode || Number(response.data) > 0)) {
      this.createdId = response.data;
    }

    /**
     * Clearing the model after submit
     * only in new entity creating case.
     * Also if we have redirect flag - we don't
     * need to clear the model
     */
    if (!this.editMode && !this.redirectToEditPage) {
      this.options.resetModel();
    }
    this.cdr.detectChanges();
    this.afterSubmit();
  }

  protected saveTerm(term: Term) {
    const url = '/term';
    this.compoApiService.post<{
      header: string
    }, Term>(url, {header: term.header}).pipe(take(1), catchError(err => this.handleError(err))).subscribe((response) => {
      if ('id' in response.data) {
        this.insertTerm(response.data);
      }
    });
  }

  protected uniqueHeaderChecker(control: AbstractControl): Observable<boolean> {
    let params = new HttpParams().set('header', control.value);
    if (this.editMode) {
      params = params.set('updatedEntityId', this.id);
    }

    return this.compoApiService.getWithParams<boolean>(this.uri + '/is-header-unique', params)
      .pipe(
        map(response => response.data),
        tap(_ => this.cdr.detectChanges())
      );
  }

  getAbsolutePath() {
    const customPath = this.config.absolutePath;
    if (customPath) {
      return customPath;
    }

    if (this.editMode) {
      return '../../'
    }

    return '../';
  }

  get uri() {
    return this.config.uri;
  }

}
